import React, { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import {
    SwipeableListItem as RawSwipeableListItem,
    LeadingActions,
    SwipeAction as RawSwipeAction,
    Type,
} from 'react-swipeable-list'
import 'react-swipeable-list/dist/styles.css'
import '../../../../../generic/swipe/swipeable-overrides.css'
import {
    SWIPE_START_THRESHOLD,
    SCROLL_START_THRESHOLD,
} from '../../../../../generic/swipe/constants'
import { SELECT_SORTING_DATA } from '../../../../../../store/actions/actionsTypes'
import { CardRow } from '../../../../../generic/ui/CardRow'
import { Mono } from '../../../../../generic/ui/Mono'
import BarcodeIcon from '../../../../../generic/icons/IconLib/BarcodeIcon'
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon'
import SproutIcon from '../../../../../generic/icons/IconLib/SproutIcon'
import ScaleIcon from '../../../../../generic/icons/IconLib/ScaleIcon'
import LinkIcon from '../../../../../generic/icons/IconLib/LinkIcon'
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon'
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon'
import { ISortingListItem } from '../../../../../modules/API/Miun/models/sortingList'
import { LINKS } from '../../../../../Routs/config'
import { convertToDate } from '../../../../../modules/convertDateString'

interface Props {
    item: ISortingListItem
    isLast?: boolean
}

const OVERLAY_THRESHOLD = 30 // % — reveal the action overlay
const noop = () => {}

const MiunSortingCard: React.FC<Props> = ({ item, isLast = false }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [actionsOpen, setActionsOpen] = useState<boolean>(false)
    const lastProgress = useRef(0)
    const lastDirection = useRef<'left' | 'right' | ''>('')

    const handleOpenEntries = () => {
        setActionsOpen(false)
        dispatch({ type: SELECT_SORTING_DATA, payload: item })
        navigate(LINKS.miun.control.create)
    }
    const handleOpenLines = () => {
        setActionsOpen(false)
        dispatch({ type: SELECT_SORTING_DATA, payload: item })
        navigate(LINKS.miun.control.lines)
    }
    const handleToggle = () => setIsOpen((v) => !v)

    const handleSwipeStart = () => setActionsOpen(false)
    const handleSwipeProgress = (progress: number, direction: string) => {
        lastProgress.current = progress
        if (direction === 'left' || direction === 'right') {
            lastDirection.current = direction
        }
    }
    const handleSwipeEnd = () => {
        const p = lastProgress.current
        const dir = lastDirection.current
        lastProgress.current = 0
        lastDirection.current = ''
        if (dir === 'right' && p >= OVERLAY_THRESHOLD) setActionsOpen(true)
    }

    const date = convertToDate(item.Date_Sorting || '00000000')
    const productLine =
        `${item.ProductDesc?.trim() ?? ''} ${item.VarietyDesc?.trim() ?? ''}`.trim()
    const grower = item.GrowerName?.trim() ?? ''
    const hasLines = (item.Lines?.length ?? 0) > 0

    const leadingActions = (
        <LeadingActions>
            <RawSwipeAction onClick={noop} Tag='div'>
                <SwipeHint>
                    <PencilIcon />
                    <PackageBoxIcon color='currentColor' size={16} />
                </SwipeHint>
            </RawSwipeAction>
        </LeadingActions>
    )

    return (
        <Wrapper>
            <RawSwipeableListItem
                leadingActions={leadingActions}
                listType={Type.ANDROID}
                threshold={1.5}
                swipeStartThreshold={SWIPE_START_THRESHOLD}
                scrollStartThreshold={SCROLL_START_THRESHOLD}
                onSwipeStart={handleSwipeStart}
                onSwipeProgress={handleSwipeProgress}
                onSwipeEnd={handleSwipeEnd}
            >
                {actionsOpen && (
                    <ActionOverlay>
                        <OverlayBtn type='button' onClick={handleOpenEntries}>
                            <PencilIcon />
                            <span>כניסות</span>
                        </OverlayBtn>
                        <OverlayBtn
                            type='button'
                            $variant='lines'
                            onClick={handleOpenLines}
                        >
                            <PackageBoxIcon color='currentColor' size={16} />
                            <span>שורות מיון</span>
                        </OverlayBtn>
                    </ActionOverlay>
                )}
                <CardRow
                    $columns='5rem minmax(0, 1fr) auto auto'
                    $mobileColumns='auto minmax(0, 1fr) auto auto'
                    $isLast={isLast && !isOpen}
                    $interactive
                    onClick={handleToggle}
                >
                    <CardRow.Cell>
                        <CardRow.Stack $gap='0.125rem'>
                            <CardRow.Line $gap='0.375rem'>
                                <IdIcon>
                                    <BarcodeIcon color='currentColor' size={16} />
                                </IdIcon>
                                <Id>{item.Sorting_Num}</Id>
                            </CardRow.Line>
                            {item.Sorting_Reference ? (
                                <CardRow.Line $gap='0.25rem'>
                                    <SubIcon>
                                        <LinkIcon color='currentColor' size={12} />
                                    </SubIcon>
                                    <Reference>{item.Sorting_Reference}</Reference>
                                </CardRow.Line>
                            ) : null}
                        </CardRow.Stack>
                    </CardRow.Cell>

                    <CardRow.Cell>
                        <CardRow.Stack $gap='0.25rem'>
                            <CardRow.Line $gap='0.5rem'>
                                <SubIcon>
                                    <SproutIcon color='currentColor' size={16} />
                                </SubIcon>
                                <PrimaryName>{productLine}</PrimaryName>
                            </CardRow.Line>
                            <CardRow.Line $gap='0.5rem'>
                                <SubIcon>
                                    <UserIcon color='currentColor' size={14} />
                                </SubIcon>
                                <SecondaryName>{grower}</SecondaryName>
                            </CardRow.Line>
                        </CardRow.Stack>
                    </CardRow.Cell>

                    <CardRow.Cell $align='center'>
                        <When>{date}</When>
                    </CardRow.Cell>

                    <CardRow.Cell $align='center'>
                        <CardRow.Stack $gap='0.125rem' $align='end'>
                            <CardRow.Line $gap='0.25rem'>
                                <SubIcon>
                                    <ScaleIcon color='currentColor' size={14} />
                                </SubIcon>
                                <Weight>{Math.round(item.Entry_WeightNeto)}</Weight>
                            </CardRow.Line>
                            <SortedWeight>
                                ממוין {Math.round(item.Sorted_WeightNeto)}
                            </SortedWeight>
                        </CardRow.Stack>
                    </CardRow.Cell>
                </CardRow>
            </RawSwipeableListItem>

            {isOpen && hasLines && (
                <LinesPanel $isLast={isLast}>
                    {item.Lines.map((line, idx) => {
                        const pct = item.Sorted_WeightNeto
                            ? Math.round((line.WeightNeto / item.Sorted_WeightNeto) * 100)
                            : 0
                        return (
                            <LineRow key={`${line.SortRow}-${idx}`}>
                                <LineMain>
                                    <LineTop>
                                        <LineTopItem>
                                            <PackageBoxIcon color='currentColor' size={14} />
                                            <span> 
                                                {`${line.QualityDesc?.trim()}  ${line.SizeDesc?.trim()} - ${line.Qty} `} 

                                            </span>
                                        </LineTopItem>
                                    </LineTop>
                                    <LineBottom>
                                        <LineBottomItem>
                                            <PackageBoxIcon color='currentColor' size={12} />
                                            <span>{line.Pack_Name?.trim()}</span>
                                        </LineBottomItem>
                                        <LineBottomItem>
                                            <ScaleIcon color='currentColor' size={12} />
                                            <Mono>{Math.round(line.WeightNeto)}</Mono>
                                        </LineBottomItem>
                                    </LineBottom>
                                </LineMain>
                                <LinePercent>{pct}%</LinePercent>
                            </LineRow>
                        )
                    })}
                </LinesPanel>
            )}
        </Wrapper>
    )
}

export default MiunSortingCard

const Wrapper = styled.div`
    position: relative;
    width: 100%;

    .swipeable-list-item,
    .swipeable-list-item__content {
        width: 100%;
    }
`

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const SwipeHint = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 1.25rem;
    width: 100%;
    height: 100%;
    padding: 0 1.25rem;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};

    svg {
        width: 1.125rem;
        height: 1.125rem;
    }
`

const ActionOverlay = styled.div`
    flex-shrink: 0;
    align-self: stretch;
    display: inline-flex;
    animation: ${fadeIn} 0.18s ease-out;
`

const OverlayBtn = styled.button<{ $variant?: 'lines' }>`
    flex-shrink: 0;
    align-self: stretch;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    width: 5rem;
    padding: 0 0.5rem;
    border: 0;
    background: ${({ theme, $variant }) =>
        $variant === 'lines' ? theme.colors.accentDeep : theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    transition:
        transform ${({ theme }) => theme.motion.fast} ease,
        filter ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    &:hover {
        filter: brightness(0.95);
    }

    &:active {
        transform: scale(0.97);
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 4rem;
    }
`

const IdIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink2};
    flex-shrink: 0;
`
const SubIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`
const Reference = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`

const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const SecondaryName = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`

const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const Weight = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 600;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const SortedWeight = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.accentDeep};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`

const LinesPanel = styled.div<{ $isLast?: boolean }>`
    background: ${({ theme }) => theme.colors.bgLayout};
    border-bottom: 1px solid
        ${({ theme, $isLast }) => ($isLast ? 'transparent' : theme.colors.line2)};
    padding: 0.5rem 0.75rem;
    direction: rtl;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`

const LineRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 3rem;
    align-items: center;
    gap: 0.625rem;
    padding: 0.375rem 0.5rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line2};
    border-radius: 0.75rem;
`

const LinePercent = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accentSoft};
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 0.8rem;
    font-weight: 600;
`

const LineMain = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
`

const LineTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
`
const LineTopItem = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`
const LineBottom = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1rem;
`
const LineBottomItem = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: ${({ theme }) => theme.colors.ink3};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`
